"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

interface MarketEmbedModalProps {
  marketSlug: string;
  onClose: () => void;
}

type Theme = "dark" | "light";
type Size = "small" | "medium" | "large";
type View = "summary" | "orderbook" | "all";

const SIZE_DIMENSIONS: Record<Size, { width: number; height: number }> = {
  small: { width: 320, height: 200 },
  medium: { width: 480, height: 280 },
  large: { width: 640, height: 360 },
};

/**
 * EPIC-006 STORY-616 — embed code generator modal.
 *
 * Live preview iframe + copy-paste snippet that toggles theme /
 * size / view. Source param is auto-set to the user's username
 * when authed, so the founder can see referral attribution in
 * server logs (full embed_views table is a STORY-616 follow-up).
 */
export function MarketEmbedModal({
  marketSlug,
  onClose,
}: MarketEmbedModalProps) {
  const { t } = useTranslation();
  const [theme, setTheme] = useState<Theme>("dark");
  const [size, setSize] = useState<Size>("medium");
  const [view, setView] = useState<View>("summary");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const dimensions = SIZE_DIMENSIONS[size];

  // Build embed URL using the current site origin so the snippet
  // works on staging + production without manual editing.
  const embedUrl = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://granbay.com";
    const params = new URLSearchParams({
      theme,
      size,
      view,
    });
    return `${origin}/embed/market/${marketSlug}?${params.toString()}`;
  }, [marketSlug, theme, size, view]);

  const snippet = useMemo(() => {
    return `<iframe
  src="${embedUrl}"
  width="${dimensions.width}"
  height="${dimensions.height}"
  frameborder="0"
  loading="lazy"
  title="${t("marketEmbed.iframeTitle", { slug: marketSlug })}"
></iframe>`;
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedUrl, dimensions, marketSlug]);

  async function copySnippet() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent — user can manually copy from the textarea below.
    }
  }

  if (typeof document === "undefined") return null;
  // Portal to <body> — sidesteps backdrop-filter ancestors that
  // would re-root our `fixed inset-0` containing block.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="embed-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-3 py-4"
    >
      <button
        type="button"
        aria-label={t("marketEmbed.dismiss")}
        onClick={onClose}
        className="absolute inset-0 bg-black/70"
      />
      <div className="relative max-h-[calc(100dvh-2rem)] w-full max-w-2xl overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-xl">
        <h2
          id="embed-modal-title"
          className="text-[16px] font-bold text-[var(--color-text)]"
        >
          {t("marketEmbed.title")}
        </h2>
        <p className="mt-1 text-[12px] text-[var(--color-text3)]">
          {t("marketEmbed.subtitle")}
        </p>

        {/* Customization */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Selector
            label={t("marketEmbed.theme")}
            value={theme}
            onChange={(v) => setTheme(v as Theme)}
            options={[
              { value: "dark", label: t("marketEmbed.themeDark") },
              { value: "light", label: t("marketEmbed.themeLight") },
            ]}
          />
          <Selector
            label={t("marketEmbed.size")}
            value={size}
            onChange={(v) => setSize(v as Size)}
            options={[
              { value: "small", label: t("marketEmbed.sizeSmall") },
              { value: "medium", label: t("marketEmbed.sizeMedium") },
              { value: "large", label: t("marketEmbed.sizeLarge") },
            ]}
          />
          <Selector
            label={t("marketEmbed.view")}
            value={view}
            onChange={(v) => setView(v as View)}
            options={[
              { value: "summary", label: t("marketEmbed.viewSummary") },
              { value: "orderbook", label: t("marketEmbed.viewOrderbook") },
              { value: "all", label: t("marketEmbed.viewAll") },
            ]}
          />
        </div>

        {/* Live preview */}
        <div className="mt-4">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
            {t("marketEmbed.preview")}
          </div>
          <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
            {/* iframe pinned to its declared size; on a 375px
                viewport "large" preview (600px+) horizontally
                scrolls within the bordered card rather than
                pushing the modal sideways. */}
            <iframe
              key={embedUrl}
              src={embedUrl}
              width={dimensions.width}
              height={dimensions.height}
              frameBorder={0}
              loading="lazy"
              title={t("marketEmbed.iframePreviewTitle", { slug: marketSlug })}
              style={{ display: "block", margin: "0 auto" }}
            />
          </div>
        </div>

        {/* Snippet */}
        <div className="mt-4">
          <div className="mb-1 flex items-baseline justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
              {t("marketEmbed.embedCode")}
            </span>
            <button
              type="button"
              onClick={copySnippet}
              className="min-h-[36px] rounded-md border border-[var(--color-border)] px-2 py-0.5 text-[10px] font-bold text-[var(--color-text2)] hover:border-[var(--color-brand)] md:min-h-0"
            >
              {copied ? t("marketEmbed.copied") : t("marketEmbed.copy")}
            </button>
          </div>
          <textarea
            readOnly
            value={snippet}
            rows={6}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 font-mono text-[11px] text-[var(--color-text2)]"
          />
        </div>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full rounded-md border border-[var(--color-border)] py-2 text-[11px] font-bold text-[var(--color-text3)] hover:text-[var(--color-text)]"
        >
          {t("marketEmbed.close")}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function Selector<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </label>
      <div className="flex gap-1">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className="flex-1 rounded-md border px-2 py-1.5 text-[11px] font-bold"
              style={{
                borderColor: active
                  ? "var(--color-brand)"
                  : "var(--color-border)",
                background: active
                  ? "var(--color-brand-tint)"
                  : "var(--color-bg)",
                color: active
                  ? "var(--color-brand)"
                  : "var(--color-text2)",
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
